import React, { PureComponent, Fragment, ChangeEvent } from 'react';
import styled from 'styled-components';
import { debounce, isEmpty } from 'lodash';
import {
  Banner,
  FormGroup,
  FormInput,
  FormHelpText,
  Type,
  IconCheckAlt,
  IconInformationAlt,
} from '@spotify-internal/encore-creator-web';
import { azure } from '@spotify-internal/encore-foundation';
import tokens from '@spotify-internal/encore-foundation';
import { RSSVerificationErrorFeedback } from '../RSSVerificationErrorFeedback';
import { LOADING } from '../../../store/selectors';
import { AnimatedAlert } from '../../Shared/AnimatedAlert';
import { LoadingIndicator } from '../../Shared/LoadingIndicator';
import { trackEvent } from '../../../lib/Tracking';
import { Podcast } from '../../../types';
import { PodcastModalBody } from '../../Shared/PodcastModal';
import { AddPodcastModalContext } from '../AddPodcastModal/context';
import { FormattedMessage, injectIntl, defineMessage, IntlShape } from 'react-intl';
import { TSFix } from '@spotify-internal/ts-migration-utils/types';
import { LOCALE_PREFERENCES } from '../../../lib/constants';

const StyledSuccess = styled.div`
  margin-top: ${tokens.spacer16};
  display: flex;
  align-items: center;
`;

const MessageIcon = styled(IconCheckAlt)`
  margin-right: ${tokens.spacer8};
`;

const RSSVerificationSuccess = () => (
  <StyledSuccess>
    <MessageIcon color="spotifyGreenHighlight" aria-label="IconCheckAlt" iconSize={16} />
    <Type
      as="p"
      data-qa="rss-feed-valid-indicator"
      color="spotifyGreenHighlight"
      variant={Type.body1}
      condensed
    >
      <FormattedMessage
        id="add-podcast.rss-verification-successful"
        defaultMessage="Looking good. Let's keep going."
      />
    </Type>
  </StyledSuccess>
);

const RSSVerificationDuplicate = () => (
  <StyledSuccess>
    <MessageIcon
      as={IconInformationAlt}
      color={azure}
      aria-label="IconInformationAlt"
      iconSize={16}
    />
    <Type as="p" data-qa="rss-feed-valid-indicator" color={azure} variant={Type.body1} condensed>
      <FormattedMessage
        id="add-podcast.rss-verification-duplicate"
        defaultMessage="Your podcast might already be on Spotify. Hit <strong>next</strong> to check"
        values={{
          strong: (...chunks) => <strong>{chunks}</strong>,
        }}
      />
    </Type>
  </StyledSuccess>
);

interface Props {
  podcastRSSLink?: string;
  onChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  validateFeed: (feedUrl: string) => void;
  rssFeed?: Podcast;
  network?: {
    name?: string;
  };
  status?: 'LOADING' | 'UNAUTHORIZED' | 'ERROR';
  intl: IntlShape;
}

class InternationalizationRSSVerification extends PureComponent<Props> {
  static contextType = AddPodcastModalContext;
  context!: React.ContextType<typeof AddPodcastModalContext>;

  componentDidMount() {
    trackEvent({ category: 'page-type', action: 'view', label: 'submit-rss' });

    this.context.setNextButtonDisabled(!this.hasValidFeed);
  }

  onChangeWithDebouncedValidation = e => {
    const { onChange } = this.props;
    onChange(e);
    this.debouncedValidation(e.target.value.trim());
  };

  get hasErrors() {
    return this.props.rssFeed && !isEmpty(this.props.rssFeed.errors);
  }

  get hasValidFeed() {
    return this.props.rssFeed && isEmpty(this.props.rssFeed.errors);
  }

  debouncedValidation = debounce(async (feedUrl: string) => {
    this.context.setNextButtonDisabled(true);

    await this.props.validateFeed(feedUrl);

    const { rssFeed } = this.props;
    if (rssFeed && !rssFeed.isExistingShow) {
      this.context.setPreviewPanePodcast(rssFeed);
    } else {
      this.context.setPreviewPanePodcast(null);
    }

    if (this.hasValidFeed) {
      this.context.setNextButtonDisabled(false);
    }
  }, 1500);

  renderMessage() {
    if (this.hasErrors) {
      return <RSSVerificationErrorFeedback errors={this.props.rssFeed!.errors} />;
    } else if (this.hasValidFeed && this.props.rssFeed!.isExistingShow) {
      return <RSSVerificationDuplicate />;
    } else if (this.hasValidFeed) {
      return <RSSVerificationSuccess />;
    } else return null;
  }

  render() {
    const { podcastRSSLink, intl } = this.props;
    const localeLegal = LOCALE_PREFERENCES[intl.locale]
      ? LOCALE_PREFERENCES[intl.locale].languageCode
      : 'us';

    return (
      <Fragment>
        {this.hasErrors && (
          <AnimatedAlert
            variant={Banner.error}
            message={
              <FormattedMessage
                id="add-podcast.rss-validation-banner-error"
                defaultMessage="To continue, correct the errors below"
              />
            }
          />
        )}
        <PodcastModalBody data-qa="rss-verification-form">
          <Type as="h1" variant={Type.heading2}>
            <FormattedMessage
              id="add-podcast.rss-validation-link-text"
              defaultMessage="What's the link to your podcast's RSS feed?"
            />
          </Type>
          {this.props.network && (
            <Type as="p" variant={Type.body1} data-qa="network-association-message">
              <FormattedMessage
                id="add-podcast.rss-validation-network-association"
                defaultMessage="The podcast you submit will be associated with
              <strong>{networkName}</strong> on Spotify."
                values={{
                  strong: (...chunks) => <strong>{chunks}</strong>,
                  networkName: this.props.network.name,
                }}
              />
            </Type>
          )}
          <Type as="p" variant={Type.body1}>
            <FormattedMessage
              id="add-podcast.rss-validation-network-ownership"
              defaultMessage="Only add a link to an RSS feed you own the rights to, and make sure your podcast content
            meets our <a>guidelines</a>. You'll only have to do this once. After your podcast is on Spotify, it will update automatically."
              values={{
                a: (...chunks) => (
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`https://www.spotify.com/${localeLegal}/legal/end-user-agreement/`}
                  >
                    {chunks}
                  </a>
                ),
              }}
            />
          </Type>
          <FormGroup
            label={
              <FormattedMessage
                id="add-podcast.rss-validation-link-title"
                defaultMessage="Link to RSS feed"
              />
            }
            labelFor="podcastRSSLink"
          >
            <FormInput
              id="podcastRSSLink"
              placeholder="https://www.mypodcast.com/rss"
              value={podcastRSSLink}
              onChange={this.onChangeWithDebouncedValidation}
              error={this.hasErrors}
              disabled={this.props.status === LOADING}
            />
            <FormHelpText error={this.hasErrors}>
              {this.props.status === LOADING && (
                <LoadingIndicator style={{ marginTop: '16px', justifyContent: 'start' }} />
              )}
              {this.renderMessage()}
            </FormHelpText>
          </FormGroup>
        </PodcastModalBody>
      </Fragment>
    );
  }
}

const RSSVerification = injectIntl(InternationalizationRSSVerification);
export { RSSVerification };
